import React, { useContext, useEffect, useState } from "react";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import Pagination from "react-js-pagination";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import jsPDF from "jspdf";
import "jspdf-autotable";

const emptyForm = (branchId, physioId = "") => ({
  referral_date: moment().format("YYYY-MM-DD"),
  trainer_id: "",
  client_id: "",
  patient_name: "",
  referred_count: "",
  session_recommended: "",
  client_referral: "",
  notes: "",
  branch_id: branchId,
  physio_id: physioId,
});

export default function PhysioDailyReferrals() {
  const { state } = useContext(NoteState);
  const { branch_id, role, UId } = state;
  const isPhysioUser = role === "15";
  const defaultPhysioId = isPhysioUser ? UId : "";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm(branch_id, defaultPhysioId));
  const [search, setSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [limit, setLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    fetchRows(1, limit);
    fetchTrainers();
    fetchPhysios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch_id]);

  const fetchRows = async (page = 1, lim = limit, override = {}) => {
    if (!branch_id) return;
    setLoading(true);
    try {
      const res = await UserServices.getPhysioDailyReferrals({
        branch_id,
        limit: lim,
        page,
        physio_id: isPhysioUser ? UId : override.physio_id || undefined,
        referral_date: (override.referral_date ?? filterDate) || undefined,
        search: (override.search ?? search) || undefined,
      });
      if (res.status === 200 && res.data.status) {
        setRows(res.data.data || []);
        if (res.data.pagination) {
          setCurrentPage(res.data.pagination.current_page || 1);
          setTotalResults(res.data.pagination.total_record || 0);
        } else {
          setCurrentPage(1);
          setTotalResults((res.data.data || []).length);
        }
      }
    } catch (err) {
      console.log(err);
      setRows([]);
    }
    setLoading(false);
  };

  const fetchTrainers = async () => {
    try {
      const res = await UserServices.getPhysioDailyReferralTrainers(branch_id);
      if (res.status === 200 && res.data.status) {
        setTrainers(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const fetchPhysios = async () => {
    try {
      const res = await UserServices.getPhysioUsers(branch_id);
      if (res.status === 200 && res.data.status) {
        setPhysios(res.data.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openAdd = () => {
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditMode(true);
    setSelected(row);
    setFormData({
      referral_date: row.referral_date ? moment(row.referral_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"),
      trainer_id: row.trainer_id || "",
      client_id: row.client_id || "",
      patient_name: row.patient_name || "",
      referred_count: row.referred_count ?? "",
      session_recommended: row.session_recommended || "",
      client_referral: row.client_referral || "",
      notes: row.notes || "",
      branch_id: row.branch_id || branch_id,
      physio_id: row.physio_id || defaultPhysioId,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelected(null);
    setFormData(emptyForm(branch_id, defaultPhysioId));
    setSaving(false);
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!formData.patient_name.trim()) {
      alert("Patient name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        referred_count: formData.referred_count === "" ? 0 : Number(formData.referred_count),
        client_id: formData.client_id || null,
        trainer_id: formData.trainer_id || null,
        physio_id: isPhysioUser ? UId : formData.physio_id || null,
      };
      if (editMode && selected) {
        await UserServices.updatePhysioDailyReferral(selected.id, payload);
      } else {
        await UserServices.createPhysioDailyReferral(payload);
      }
      closeModal();
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert(err?.response?.data?.message || "Failed to save referral");
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this referral row?")) return;
    try {
      await UserServices.deletePhysioDailyReferral(id);
      fetchRows(currentPage, limit);
    } catch (err) {
      console.log(err);
      alert("Failed to delete referral");
    }
  };

  const handleExcel = async () => {
    try {
      const res = await UserServices.exportPhysioDailyReferrals({
        branch_id,
        physio_id: isPhysioUser ? UId : undefined,
        referral_date: filterDate || undefined,
        search: search || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Physio_Daily_Referral_${moment().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.log(err);
      alert("Failed to export Excel");
    }
  };

  const handlePdf = () => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      doc.setFontSize(16);
      doc.text("Daily Client Referral", 14, 16);
      doc.autoTable({
        startY: 22,
        head: [["S#", "Date", "Patient", "Trainer", "Referred", "Session Recommended", "Client Referral", "Notes"]],
        body: rows.map((r, i) => [
          (currentPage - 1) * limit + i + 1,
          r.referral_date ? moment(r.referral_date).format("DD/MM/YY") : "",
          r.patient_name || "",
          r.trainer ? `${r.trainer.first_name || ""} ${r.trainer.last_name || ""}`.trim() : "",
          r.referred_count ?? 0,
          r.session_recommended || "",
          r.client_referral || "",
          r.notes || "",
        ]),
        headStyles: { fillColor: [132, 76, 156], textColor: 255 },
        styles: { fontSize: 8 },
      });
      doc.save(`Physio_Daily_Referral_${moment().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.log(err);
      alert("Failed to generate PDF");
    }
  };

  return (
    <div className="site-main-design">
      <MenuButton />
      <div className="container-fluid">
        <div className="page-heading d-flex justify-content-between align-items-center mb-3">
          <h3 className="mb-0">
            <i className="bi bi-journal-check me-2"></i>
            Daily Client Referral
          </h3>
          <button className="btn btn-primary" onClick={openAdd}>
            <i className="bi bi-plus-circle me-1"></i>Add Record
          </button>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Patient or client referral..."
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={filterDate}
                  onChange={(e) => setFilterDate(e.target.value)}
                />
              </div>
              <div className="col-md-5 d-flex gap-2">
                <button className="btn btn-primary" onClick={() => fetchRows(1, limit)}>Search</button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSearch("");
                    setFilterDate("");
                    fetchRows(1, limit, { search: "", referral_date: "" });
                  }}
                >
                  Clear
                </button>
                <button className="btn btn-success" onClick={handleExcel}>Excel</button>
                <button className="btn btn-danger" onClick={handlePdf}>PDF</button>
              </div>
            </div>
          </div>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status" />
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No referral records found.</div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>S#</th>
                        <th>Date</th>
                        <th>Patient</th>
                        <th>Trainer</th>
                        <th>Referred</th>
                        <th>Session Recommended</th>
                        <th>Client Referral</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={r.id}>
                          <td>{(currentPage - 1) * limit + i + 1}</td>
                          <td>{r.referral_date ? moment(r.referral_date).format("DD/MM/YY") : "—"}</td>
                          <td>{r.patient_name || "—"}</td>
                          <td>{r.trainer ? `${r.trainer.first_name || ""} ${r.trainer.last_name || ""}`.trim() : "—"}</td>
                          <td>{r.referred_count ?? 0}</td>
                          <td>{r.session_recommended || "—"}</td>
                          <td>{r.client_referral || "—"}</td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <button className="btn btn-outline-primary" onClick={() => openEdit(r)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-outline-danger" onClick={() => handleDelete(r.id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalResults > limit ? (
                  <div className="d-flex justify-content-center">
                    <Pagination
                      activePage={currentPage}
                      itemsCountPerPage={limit}
                      totalItemsCount={totalResults}
                      pageRangeDisplayed={5}
                      onChange={(p) => {
                        setCurrentPage(p);
                        fetchRows(p, limit);
                      }}
                      itemClass="page-item"
                      linkClass="page-link"
                    />
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <Modal show={showModal} onHide={closeModal} size="lg" backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? "Edit Daily Referral" : "Add Daily Referral"}</Modal.Title>
          </Modal.Header>
          <form onSubmit={submit}>
            <Modal.Body>
              <div className="row g-3">
                {!isPhysioUser ? (
                  <div className="col-md-6">
                    <label className="form-label">Physiotherapist</label>
                    <select
                      className="form-select"
                      name="physio_id"
                      value={formData.physio_id}
                      onChange={handleInput}
                      required
                    >
                      <option value="">Select physiotherapist</option>
                      {physios.map((p) => (
                        <option key={p.id} value={p.id}>
                          {`${p.first_name || ""} ${p.last_name || ""}`.trim()}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                <div className="col-md-6">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="referral_date"
                    value={formData.referral_date}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Patient Name</label>
                  <input
                    className="form-control"
                    name="patient_name"
                    value={formData.patient_name}
                    onChange={handleInput}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Trainer</label>
                  <select
                    className="form-select"
                    name="trainer_id"
                    value={formData.trainer_id}
                    onChange={handleInput}
                  >
                    <option value="">Select trainer</option>
                    {trainers.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Referred Count</label>
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    name="referred_count"
                    value={formData.referred_count}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Session Recommended</label>
                  <input
                    className="form-control"
                    name="session_recommended"
                    value={formData.session_recommended}
                    onChange={handleInput}
                    placeholder="consultation / 10 / etc."
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Client Referral</label>
                  <input
                    className="form-control"
                    name="client_referral"
                    value={formData.client_referral}
                    onChange={handleInput}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInput}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : editMode ? "Update" : "Save"}
              </Button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </div>
  );
}
